import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { defineMessages } from "react-intl"
import _ from "lodash"
import Box from "@material-ui/core/Box"

import ConnectedSelector from "../../component/widgets/ConnectedSelector"
import DateSelectorWidget from "../../component/widgets/DateSelectorWidget"
import widgetSlice, {
  WEALTH_CUSTOMER_SELECTOR_ID,
  WEALTH_END_DATE_SELECTOR_ID,
  WEALTH_END_MONTH_DATE_SELECTOR_ID,
  WEALTH_PORTFOLIO_SELECTOR_ID,
  WEALTH_YEAR_SELECTOR_ID
} from "../../api/widgetSlice"
import { CancelToken } from "../../api/httpClient"

const messages = defineMessages({
  portfolios: {
    id: "selector.label.portfolios",
    defaultMessage: "Your portfolios"
  },
  closedPortfolios: {
    id: "selector.label.closed.portfolios",
    defaultMessage: "Your closed portfolios"
  },
  customers: {
    id: "selector.label.customer",
    defaultMessage: "Customers"
  },
  mainAccounts: {
    id: "selector.label.account.main",
    defaultMessage: "Your accounts"
  },
  indirectAccounts: {
    id: "selector.label.account.indirect",
    defaultMessage: "Accounts of your family"
  }
})

const { addWidgetMessage, updateSelectorSelection } = widgetSlice.actions
const { selectorOf, selectedValueOf } = widgetSlice.select
const groupByPortfolio = (entries) => _.groupBy(entries, "customerId")
const groupByCustomer = (entries) => _.groupBy(entries, "mainContact")

const emptyCustomerMessage = "customer selector is empty"
const emptyPortfolioMessage = "portfolio selector is empty"

function addMessage(message) {
  return (dispatch) => dispatch(addWidgetMessage(message))
}

const onEmptyPortfolio = addMessage(emptyPortfolioMessage)

export const CustomersSelector = (props) => {
  const dispatch = useDispatch()
  // we must make sure the store for the portfolio selector is initilized
  // otherwise the customer selector does not work.
  const portfoliosSelector = useSelector(
    selectorOf(WEALTH_PORTFOLIO_SELECTOR_ID)
  )

  useEffect(() => {
    const { token, cancel } = CancelToken.source()
    if (!portfoliosSelector) {
      dispatch(
        widgetSlice.actions.fetchSelectorValues(
          WEALTH_PORTFOLIO_SELECTOR_ID,
          token,
          groupByPortfolio,
          null
        )
      )
    }

    return () => cancel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, WEALTH_PORTFOLIO_SELECTOR_ID])

  const handleCustomerChange = (event) => {
    const newCustomerId = event.target.value
    const portfolios = portfoliosSelector.values
    const newPortfolioId = portfolios?.find(
      ({ customerId }) => customerId === newCustomerId
    )?.value
    dispatch(
      updateSelectorSelection(WEALTH_CUSTOMER_SELECTOR_ID, newCustomerId)
    )
    if (newPortfolioId) {
      dispatch(
        updateSelectorSelection(WEALTH_PORTFOLIO_SELECTOR_ID, newPortfolioId)
      )
    }
  }

  return (
    <ConnectedSelector
      id={WEALTH_CUSTOMER_SELECTOR_ID}
      onEmpty={addMessage(emptyCustomerMessage)}
      label={messages.customers}
      className="connected-selector-min-width"
      onChange={handleCustomerChange}
      groupBy={(entries) => _.groupBy(entries, "mainContact")}
      // this structure has been chosen to keep the order
      // a basic K, V plain object does not keep order of property
      groupCategoryLabel={{
        true: messages.mainAccounts,
        false: messages.indirectAccounts
      }}
      staticValue
      {...props}
    />
  )
}

export const PortfoliosSelector = (props) => {
  const dispatch = useDispatch()
  const portfoliosSelector = useSelector(
    selectorOf(WEALTH_PORTFOLIO_SELECTOR_ID)
  )
  const customers = useSelector(selectorOf(WEALTH_CUSTOMER_SELECTOR_ID))

  useEffect(() => {
    const { token } = CancelToken.source()
    if (!customers) {
      dispatch(
        widgetSlice.actions.fetchSelectorValues(
          WEALTH_CUSTOMER_SELECTOR_ID,
          token,
          groupByCustomer,
          null
        )
      )
    }
  }, [dispatch, customers])

  const handlePortfolioChange = (event) => {
    let portfolioId = event.target.value
    const portfolios = portfoliosSelector.values

    if (!portfolioId && portfolios.length !== 0) {
      portfolioId = portfolios[0].value
    }
    const newCustomerId = portfolios?.find(
      ({ value }) => value === portfolioId
    )?.customerId
    dispatch(updateSelectorSelection(WEALTH_PORTFOLIO_SELECTOR_ID, portfolioId))
    if (newCustomerId) {
      dispatch(
        updateSelectorSelection(WEALTH_CUSTOMER_SELECTOR_ID, newCustomerId)
      )
    }
  }
  const customerMap = {}
  if (customers && Array.isArray(customers.values)) {
    customers.values.forEach((customer) => {
      customerMap[customer.value] = customer.text
    })
  }

  return (
    <ConnectedSelector
      id={WEALTH_PORTFOLIO_SELECTOR_ID}
      onEmpty={onEmptyPortfolio}
      label={messages.portfolios}
      className="connected-selector-min-width"
      groupBy={groupByPortfolio}
      groupCategoryLabel={customerMap}
      onChange={handlePortfolioChange}
      staticValue
      {...props}
    />
  )
}

export const DateWithPortfolios = (props) => {
  const endDateValue = useSelector(selectedValueOf(WEALTH_END_DATE_SELECTOR_ID))

  return (
    <>
      <Box mr={1}>
        <DateSelectorWidget
          widgetId={WEALTH_END_DATE_SELECTOR_ID}
          defaultValue={endDateValue.toString()}
          label="Date"
          {...props}
        />
      </Box>
      <PortfoliosSelector {...props} />
    </>
  )
}

export function YearSelector(props) {
  const yearValue = useSelector(selectedValueOf(WEALTH_YEAR_SELECTOR_ID))
  return (
    <DateSelectorWidget
      widgetId={WEALTH_YEAR_SELECTOR_ID}
      views={["year"]}
      format="YYYY"
      label="Date"
      defaultValue={yearValue.toString()}
      className="date-selector-min-width"
      {...props}
    />
  )
}

export function YearPortfolioSelector(props) {
  const endDateValue = useSelector(selectedValueOf(WEALTH_END_DATE_SELECTOR_ID))
  return (
    <>
      <Box mr={1}>
        <DateSelectorWidget
          widgetId={WEALTH_END_DATE_SELECTOR_ID}
          views={["year"]}
          format="YYYY"
          label="Date"
          defaultValue={endDateValue.toString()}
          className="date-selector-min-width"
          {...props}
        />
      </Box>
      <PortfoliosSelector {...props} />
    </>
  )
}

export const MonthsPortfoliosSelector = (props) => {
  const endDateValue = useSelector(
    selectedValueOf(WEALTH_END_MONTH_DATE_SELECTOR_ID)
  )
  return (
    <>
      <Box mr={1}>
        <DateSelectorWidget
          views={["month", "year"]}
          format="MMM YYYY"
          widgetId={WEALTH_END_MONTH_DATE_SELECTOR_ID}
          defaultValue={endDateValue.toString()}
          label="Date"
          className="date-selector-min-width"
          {...props}
        />
      </Box>
      <PortfoliosSelector {...props} />
    </>
  )
}
